import React, { useEffect, useState } from "react";
import { api, formatUAH } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import PeriodFilter from "@/components/PeriodFilter";
import Profitability from "@/pages/Profitability";

function Stat({ label, value, tone = "navy" }) {
  const cls = {
    navy: "text-[#0F2027]",
    teal: "text-[#0D9488]",
    green: "text-emerald-700",
    red: "text-red-600",
  }[tone];
  return (
    <Card className="gv-card">
      <CardContent className="p-6">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</div>
        <div className={`gv-kpi-value ${cls}`}>{formatUAH(value)}</div>
      </CardContent>
    </Card>
  );
}

export default function Finances() {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState({});

  useEffect(() => {
    if (period.from === undefined && period.to === undefined) return;
    const params = new URLSearchParams();
    if (period.from) params.set("from_date", period.from);
    if (period.to) params.set("to_date", period.to);
    api.get(`/finances/overview?${params}`).then((r) => setD(r.data));
  }, [period]);

  return (
    <div data-testid="finances-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Фінанси</h1>
          <p className="text-sm text-slate-500">Загальний фінансовий стан мережі Global Vision</p>
        </div>
      </div>
      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" data-testid="fin-tab-overview">Огляд</TabsTrigger>
          <TabsTrigger value="profitability" data-testid="fin-tab-profit">Прибутковість</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4">
          <FinancesOverview period={period} setPeriod={setPeriod} d={d} />
        </TabsContent>
        <TabsContent value="profitability" className="mt-4"><Profitability /></TabsContent>
      </Tabs>
    </div>
  );
}

function FinancesOverview({ period, setPeriod, d }) {
  return (
    <div className="space-y-6">
      <div className="flex justify-end"><PeriodFilter value={period} onChange={setPeriod} defaultPreset="ytd" /></div>
      {!d && <div className="text-slate-500">Завантаження...</div>}
      {d && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Виручка" value={d.revenue} />
            <Stat label="Собівартість" value={d.cost} tone="red" />
            <Stat label="Валовий прибуток" value={d.gross_profit} tone="teal" />
            <Stat label="Чистий прибуток" value={d.net_profit} tone="green" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Фонд ЗП" value={d.salary_fund} />
            <Stat label="Нараховано ЗП" value={d.salary_accrued} />
            <Stat label="Виплачено ЗП" value={d.salary_paid} tone="green" />
            <Stat label="Борг по ЗП" value={d.salary_debt} tone="red" />
          </div>

          <Card className="gv-card">
            <CardContent className="p-6">
              <h3 className="heading font-semibold text-[#0F2027] mb-4">Динаміка по місяцях</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                    <Tooltip formatter={(v) => formatUAH(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                    <Legend />
                    <Bar dataKey="revenue" name="Виручка" fill="#0D9488" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="cost" name="Собівартість" fill="#94A3B8" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="gross_profit" name="Прибуток" fill="#10B981" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
